import type { ChordRef, Place } from "@bandstand/core/arrangeChords";
import type { MouseEvent, PointerEvent } from "react";
import { useState } from "react";

import type { Arranging } from "../sheet/SheetLineView";
import type { Caret } from "./caretAt";
import { caretAt } from "./caretAt";

/** A chord on the sheet, with its name. */
export type PickedChord = { ref: ChordRef; name: string };

/** Where a dragged chord would go: a spot between two letters, or the bin. */
type Target = { kind: "caret"; caret: Caret } | { kind: "bin" };

/** A chord held down. It's dragged once it has moved a little; until then, letting go is a tap. */
type Grab = PickedChord & { x: number; y: number; startX: number; startY: number; dragging: boolean };

/** How far a chord has to move before it's dragged instead of tapped. */
const DRAG_PX = 6;

/**
 * Moving and removing chords, with a finger or the mouse: drag a chord to any spot in the lyrics or onto the bin, or
 * tap it and then tap where it goes. While dragging, a cursor between the letters shows where it lands.
 * Spread `handlers` on an element around the sheet and the bin; mark the sheet with `data-sheet` and the bin with
 * `data-bin`.
 */
export function useArrangeGestures(actions: { move(chord: ChordRef, to: Place): void; remove(chord: ChordRef): void }) {
    const [picked, setPicked] = useState<PickedChord>();
    const [grab, setGrab] = useState<Grab>();
    const [over, setOver] = useState<Target>();
    const dragging = grab?.dragging ? grab : undefined;

    function drop(chord: ChordRef, target: Target | undefined) {
        if (target?.kind === "bin") actions.remove(chord);
        else if (target) actions.move(chord, target.caret.place);

        setPicked(undefined);
    }

    function stop() {
        setGrab(undefined);
        setOver(undefined);
    }

    const handlers = {
        onPointerDown(event: PointerEvent) {
            const chord = chordOf(event.target);

            if (!chord) return;

            event.preventDefault();
            (event.target as HTMLElement).setPointerCapture(event.pointerId);
            setGrab({ ...chord, x: event.clientX, y: event.clientY, startX: event.clientX, startY: event.clientY, dragging: false });
        },
        onPointerMove(event: PointerEvent) {
            if (!grab) return;

            const moved = Math.hypot(event.clientX - grab.startX, event.clientY - grab.startY) > DRAG_PX;

            if (!grab.dragging && !moved) return;

            setGrab({ ...grab, x: event.clientX, y: event.clientY, dragging: true });
            setOver(targetAt(event.clientX, event.clientY));
        },
        onPointerUp() {
            if (!grab) return;

            if (grab.dragging) drop(grab.ref, over);
            else setPicked(sameChord(picked?.ref, grab.ref) ? undefined : grab);

            stop();
        },
        onPointerCancel: stop,
        /**
         * With a chord picked, tapping the lyrics moves it to the spot tapped, tapping the bin removes it, and tapping
         * elsewhere on the sheet lets go of it. Other controls, like the bass notes, keep it picked.
         */
        onClick(event: MouseEvent) {
            const element = event.target as HTMLElement;

            if (!picked || element.closest("[data-nth]")) return;

            const target = element.closest("[data-at], [data-bin]") ? targetAt(event.clientX, event.clientY) : undefined;

            if (target) drop(picked.ref, target);
            else if (element.closest("[data-sheet]")) setPicked(undefined);
        },
    };

    const arranging: Arranging = { picked: picked?.ref, dragged: dragging?.ref };

    return {
        handlers,
        arranging,
        picked,
        unpick: () => setPicked(undefined),
        dragging,
        caret: dragging && over?.kind === "caret" ? over.caret : undefined,
        overBin: over?.kind === "bin",
    };
}

function sameChord(a: ChordRef | undefined, b: ChordRef): boolean {
    return a?.line === b.line && a.at === b.at && a.nth === b.nth;
}

/** The chord an event happened on, if it was one. */
function chordOf(target: EventTarget): PickedChord | undefined {
    const chord = (target as HTMLElement).closest<HTMLElement>("[data-nth]");
    const word = chord?.closest<HTMLElement>("[data-at]");

    if (!chord || !word) return undefined;

    const ref = { line: Number(word.dataset.line), at: Number(word.dataset.at), nth: Number(chord.dataset.nth) };

    return { ref, name: chord.textContent ?? "" };
}

/** The bin, or the spot in the lyrics nearest to a point, as long as the point is on the sheet. */
function targetAt(x: number, y: number): Target | undefined {
    const element = document.elementFromPoint(x, y);
    const sheet = element?.closest<HTMLElement>("[data-sheet]");

    if (element?.closest("[data-bin]")) return { kind: "bin" };

    const caret = sheet ? caretAt(sheet, x, y) : undefined;

    return caret && { kind: "caret", caret };
}
